import { t } from "@lingui/macro";
import React, { useCallback, useMemo } from "react";

import { Button } from "@whoosh/design-system/src/components/controls/Button/Button";
import {
  DropdownSelect,
  DropdownSelectOption,
  DropdownSelectOptionsGroup,
} from "@whoosh/design-system/src/components/controls/DropdownSelect";
import { PlusIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import {
  MultiCourseRequestSelectorProps,
  RequestCoursePreferenceInput,
} from "./types";

export type { MultiCourseRequestSelectorProps, RequestCoursePreferenceInput };
export type {
  CourseOption,
  CourseBucketOption,
  MultiCourseRequestSelectorCopyOverrides,
} from "./types";

const REMOVE_VALUE = "__REMOVE__";
const ANY_VALUE = "__ANY__";

const ordinal = (n: number): string => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

/**
 * Converts a preference row into its dropdown value string.
 * "Any" = ANY_VALUE, courseBucket = `bucket:{id}`, course = course id
 */
const preferenceToValue = (pref: RequestCoursePreferenceInput): string => {
  if (pref.courseId) return pref.courseId;
  if (pref.courseBucketId) return `bucket:${pref.courseBucketId}`;
  return ANY_VALUE;
};

/**
 * Converts a dropdown value string back to a RequestCoursePreferenceInput.
 */
const valueToPreference = (value: string): RequestCoursePreferenceInput => {
  if (value === ANY_VALUE) return { courseId: null, courseBucketId: null };
  if (value.startsWith("bucket:"))
    return { courseId: null, courseBucketId: value.replace("bucket:", "") };
  return { courseId: value, courseBucketId: null };
};

export const MultiCourseRequestSelector = ({
  courses,
  courseBuckets,
  onChange,
  value,
  copyOverrides,
  testID = "multi-course-request-selector",
}: MultiCourseRequestSelectorProps) => {
  const courseSingular = copyOverrides?.singular ?? "Course";
  const coursePlural = copyOverrides?.plural ?? "Courses";

  const preferences: RequestCoursePreferenceInput[] = useMemo(
    () =>
      value && value.length > 0
        ? value
        : [{ courseId: null, courseBucketId: null }],
    [value]
  );

  const maxPreferences = courses.length + 1;

  const removeOption: DropdownSelectOption<string> = useMemo(
    () => ({
      value: REMOVE_VALUE,
      label: (
        <Text variant="mainSmallSemiBold" color="text-error">
          {t`Remove`}
        </Text>
      ),
    }),
    []
  );

  // Build the shared option groups once — everything except the per-row "Remove" option
  const baseGroups = useMemo((): DropdownSelectOptionsGroup<string>[] => {
    const bucketCourseIds = new Set(
      courseBuckets.flatMap((b) => b.courses.map((c) => c.id))
    );

    const groups: DropdownSelectOptionsGroup<string>[] = [
      {
        label: "",
        options: [{ value: ANY_VALUE, label: t`Any ${courseSingular}` }],
      },
    ];

    for (const bucket of courseBuckets) {
      groups.push({
        label: bucket.name,
        options: [
          {
            value: `bucket:${bucket.id}`,
            label: t`Any ${courseSingular.toLowerCase()} in ${bucket.name}`,
          },
          ...bucket.courses.map((course) => ({
            value: course.id,
            label: course.name,
          })),
        ],
      });
    }

    const unbucketedCourses = courses.filter((c) => !bucketCourseIds.has(c.id));
    if (unbucketedCourses.length > 0) {
      groups.push({
        label: "",
        options: unbucketedCourses.map((course) => ({
          value: course.id,
          label: course.name,
        })),
      });
    }

    return groups;
  }, [courses, courseBuckets, courseSingular]);

  // Collect selected values from all rows for deduplication
  const selectedValues = useMemo(
    () => new Set(preferences.map(preferenceToValue)),
    [preferences]
  );

  // Per-row options: filter out values already selected in other rows
  const buildOptions = useCallback(
    (rowIndex: number): DropdownSelectOptionsGroup<string>[] => {
      const currentValue = preferenceToValue(preferences[rowIndex]);

      const filterOptions = (
        options: DropdownSelectOption<string>[]
      ): DropdownSelectOption<string>[] =>
        options.filter(
          (opt) =>
            opt.value === currentValue ||
            opt.value === ANY_VALUE ||
            !selectedValues.has(opt.value)
        );

      const [firstGroup, ...rest] = baseGroups;

      const filteredFirst = {
        ...firstGroup,
        options:
          rowIndex > 0
            ? [removeOption, ...filterOptions(firstGroup.options)]
            : filterOptions(firstGroup.options),
      };

      const filteredRest = rest
        .map((group) => ({
          ...group,
          options: filterOptions(group.options),
        }))
        .filter((group) => group.options.length > 0);

      return [filteredFirst, ...filteredRest];
    },
    [baseGroups, removeOption, preferences, selectedValues]
  );

  const handleChange = useCallback(
    (rowIndex: number, selectedValue: string) => {
      if (selectedValue === REMOVE_VALUE) {
        const next = preferences.filter((_, i) => i !== rowIndex);
        onChange(next);
        return;
      }

      const next = [...preferences];
      next[rowIndex] = valueToPreference(selectedValue);
      onChange(next);
    },
    [preferences, onChange]
  );

  const handleAddRow = useCallback(() => {
    onChange([...preferences, { courseId: null, courseBucketId: null }]);
  }, [preferences, onChange]);

  return (
    <VStack gap={3} padding={4} testID={testID}>
      <VStack gap={1}>
        <Text variant="mainBaseSemiBold">{t`Requested ${coursePlural}`}</Text>
        <Text variant="mainBaseMedium" color="text-primary">
          {t`Only selected ${coursePlural.toLowerCase()} will be considered`}
        </Text>
      </VStack>

      <VStack gap={2}>
        {preferences.map((pref, index) => (
          <HStack key={index} alignItems="center" gap={2}>
            <Box width={32}>
              <Text variant="mainSmallSemiBold" color="text-primary">
                {ordinal(index + 1)}
              </Text>
            </Box>
            <Box flex={1}>
              <DropdownSelect<string>
                value={preferenceToValue(pref)}
                options={buildOptions(index)}
                onChange={(val) => handleChange(index, val)}
                size="small"
                testID={`${testID}-row-${index}`}
                hideOuterMargin
              />
            </Box>
          </HStack>
        ))}
      </VStack>

      <Box>
        <Button
          variant="secondary"
          size="small"
          leftIcon={<PlusIcon />}
          onPress={handleAddRow}
          isDisabled={preferences.length >= maxPreferences}
          accessibilityLabel={t`Add ${courseSingular.toLowerCase()} preference`}
          testID={`${testID}-add-button`}
        />
      </Box>
    </VStack>
  );
};
